import { getProgress, updateProgress } from '../api/abs';
import { AbsError, sessionIdentity } from '../api/client';
import { getSession } from '../api/session';
import type { MediaProgress } from '../api/types';
import { dbGet, dbGetAll, dbPut, dbUpdate } from '../lib/db';

/** Locally saved position; survives offline listening and app restarts. */
export interface LocalProgress {
  itemId: string;
  currentTime: number;
  duration: number;
  isFinished: boolean;
  lastUpdate: number;
  /** True when this position has not reached the server yet. */
  pendingSync: boolean;
  /**
   * The account (`serverUrl|userId`) that saved it. Positions are kept per
   * account, so a device shared by several listeners never resumes one at
   * another's position or sends one's offline position under another's
   * account. Rows saved before this field existed belong to whoever is
   * signed in, as they always did.
   */
  account?: string;
}

/** Whether `p` was saved by the signed-in account. */
function ownedByCurrentAccount(p: LocalProgress): boolean {
  return p.account === undefined || p.account === sessionIdentity(getSession());
}

export async function getLocalProgress(itemId: string): Promise<LocalProgress | undefined> {
  try {
    const p = await dbGet<LocalProgress>('progress', itemId);
    return p && ownedByCurrentAccount(p) ? p : undefined;
  } catch {
    return undefined;
  }
}

export async function saveLocalProgress(p: Omit<LocalProgress, 'lastUpdate' | 'account'> & { lastUpdate?: number }): Promise<void> {
  const account = sessionIdentity(getSession()) ?? undefined;
  try {
    await dbPut('progress', { ...p, account, lastUpdate: p.lastUpdate ?? Date.now() });
  } catch (err) {
    console.warn('[progress] could not save locally', err);
  }
}

export async function markSynced(itemId: string, syncedAt: number): Promise<void> {
  try {
    await dbUpdate<LocalProgress>('progress', itemId, (prev) =>
      prev && ownedByCurrentAccount(prev) && prev.lastUpdate <= syncedAt ? { ...prev, pendingSync: false } : prev,
    );
  } catch {
    /* ignore */
  }
}

/**
 * Picks where to resume: whichever of the server and local positions was
 * updated most recently. This is what lets offline listening win over a
 * stale server position, and listening on another device win over ours.
 * A local position that already reached the server is never newer than the
 * server's own (whose clock may differ from this device's), so only one still
 * waiting to sync can win.
 */
export function pickResumePosition(server: Pick<MediaProgress, 'currentTime' | 'lastUpdate' | 'isFinished'> | undefined, local: LocalProgress | undefined): number {
  const serverTime = server && !server.isFinished ? server.currentTime : 0;
  if (!local) return serverTime;
  if (!server) return local.isFinished ? 0 : local.currentTime;
  if (local.pendingSync && local.lastUpdate > server.lastUpdate) return local.isFinished ? 0 : local.currentTime;
  return serverTime;
}

let flushing: Promise<number> | null = null;

const sendProgress = (p: LocalProgress) =>
  updateProgress(p.itemId, {
    currentTime: p.currentTime,
    duration: p.duration,
    progress: p.duration > 0 ? Math.min(1, p.currentTime / p.duration) : 0,
    isFinished: p.isFinished,
  });

/** Pushes offline positions to ABS. Returns how many were synced. */
export function flushPendingProgress(): Promise<number> {
  if (flushing) return flushing;
  flushing = (async () => {
    let synced = 0;
    let pending: LocalProgress[] = [];
    try {
      // Another account's offline positions wait until that account is signed in again.
      pending = (await dbGetAll<LocalProgress>('progress')).filter((p) => p.pendingSync && ownedByCurrentAccount(p));
    } catch {
      return 0;
    }
    for (const p of pending) {
      try {
        let server: MediaProgress | undefined;
        try {
          server = await getProgress(p.itemId);
        } catch (err) {
          if (!(err instanceof AbsError && err.status === 404)) throw err;
        }
        if (!server || server.lastUpdate < p.lastUpdate) {
          // The player may have saved (and be syncing) a newer position since this flush
          // read the store, e.g. a pause landing right after the `online` event. Sending
          // the older one now would rewind the server, so leave newer positions to their
          // own sync or the next flush.
          const current = await getLocalProgress(p.itemId);
          if (!current?.pendingSync || current.lastUpdate !== p.lastUpdate) continue;
          await sendProgress(p);
          // A newer position saved while the request was in flight must be the last one
          // the server sees.
          const after = await getLocalProgress(p.itemId);
          if (after && after.lastUpdate > p.lastUpdate) {
            await sendProgress(after);
            await markSynced(p.itemId, after.lastUpdate);
            synced++;
            continue;
          }
        }
        await markSynced(p.itemId, p.lastUpdate);
        synced++;
      } catch (err) {
        if (err instanceof AbsError && err.isNetworkError) break; // still offline
        console.warn('[progress] sync failed for', p.itemId, err);
      }
    }
    return synced;
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}
